import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams, useNavigate } from "react-router-dom";
import { getActionableError } from "@/lib/errors";
import { scoreToRating, scoreToTone } from "@/lib/scoreGrade";
import Nav from "@/components/Nav";
import PublicDemoNav from "@/components/PublicDemoNav";
import BrandLockup from "@/components/BrandLockup";
import PublicThemeControl from "@/components/PublicThemeControl";
import LiveDemoFlowNav from "@/components/LiveDemoFlowNav";
import LiveDemoNextAction from "@/components/LiveDemoNextAction";
import ConversationHistoryTimeline from "@/components/ConversationHistoryTimeline";
import PrepareNextCall from "@/components/PrepareNextCall";
import RelationshipBookmark from "@/components/RelationshipBookmark";
import { DEMO_CALL_BASE, DEMO_RELATIONSHIP_DATA } from "@/data/liveDemoData";
import { SALES_DEMO_CALL_BASE, SALES_DEMO_RELATIONSHIP_DATA } from "@/data/salesLiveDemoData";
import { trackPublicEvent } from "@/lib/publicAnalytics";
import { trackUmamiEvent } from "@/lib/umamiAnalytics";
import { toast } from "sonner";
import {
  ArrowClockwise,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  Buildings,
  CheckCircle,
  CaretDown,
  ClockCounterClockwise,
  ChatCircle,
  CurrencyCircleDollar,
  Equals,
  Handshake,
  HeartBreak,
  LinkSimple,
  SealCheck,
  UsersThree,
  WarningCircle,
  Flag,
  Lightbulb,
  Phone,
  Plus,
  UserCircle,
  Warning,
  LockSimple,
  PhoneCall,
} from "@phosphor-icons/react";

const api = new Proxy({}, {
  get() {
    return async () => {
      throw new Error("The public live demo is local-only and cannot call the backend.");
    };
  },
});

const RENEWALS_DEMO_ACTIVE_CALL_ID = (
  DEMO_RELATIONSHIP_DATA.relationship_memory?.last_processed_call_id
  || DEMO_RELATIONSHIP_DATA.milestones?.[0]?.call_id
);
const SALES_DEMO_ACTIVE_CALL_ID = (
  SALES_DEMO_RELATIONSHIP_DATA.relationship_memory?.last_processed_call_id
  || SALES_DEMO_RELATIONSHIP_DATA.milestones?.[0]?.call_id
);

function isAvailableDemoCall(callId, activeCallId = null) {
  const normalized = String(callId || "");
  if (activeCallId) return normalized === String(activeCallId);
  return normalized === String(RENEWALS_DEMO_ACTIVE_CALL_ID)
    || normalized === String(SALES_DEMO_ACTIVE_CALL_ID);
}

function demoBasePathForCall(callId) {
  return String(callId || "") === String(SALES_DEMO_ACTIVE_CALL_ID)
    ? "/demo/sales"
    : "/demo/renewals";
}

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function normalizeGermanSellerRole(value) {
  if (typeof value !== "string") return value;

  return value
    .replace(/\bDer Commercial\b/g, "Der Vertriebsmitarbeiter")
    .replace(/\bder Commercial\b/g, "der Vertriebsmitarbeiter")
    .replace(/\bDen Commercial\b/g, "Den Vertriebsmitarbeiter")
    .replace(/\bden Commercial\b/g, "den Vertriebsmitarbeiter")
    .replace(/\bDem Commercial\b/g, "Dem Vertriebsmitarbeiter")
    .replace(/\bdem Commercial\b/g, "dem Vertriebsmitarbeiter")
    .replace(/\bEin Commercial\b/g, "Ein Vertriebsmitarbeiter")
    .replace(/\bein Commercial\b/g, "ein Vertriebsmitarbeiter")
    .replace(/\bEinen Commercial\b/g, "Einen Vertriebsmitarbeiter")
    .replace(/\beinen Commercial\b/g, "einen Vertriebsmitarbeiter");
}

function actionText(value) {
  if (typeof value !== "string") return value;

  const cleaned = normalizeGermanSellerRole(value)
    .replace(/^(?:(?:the|il|le|el|der)\s+)?(?:rep|sales rep|sales representative|account manager|salesperson|sales agent|commerciale|commercial|representante comercial|vertriebsmitarbeiter)\s*:\s*/i, "")
    .replace(/^(?:(?:the|il|le|el|der)\s+)?(?:rep|sales rep|sales representative|account manager|salesperson|sales agent|commerciale|commercial|representante comercial|vertriebsmitarbeiter)\s+(?:must|should|will|needs to|committed to|deve|dovrà|dovrebbe|si è impegnato a|doit|devra|s'est engagé à|debe|deberá|se comprometió a|muss|soll|wird)\s+/i, "")
    .trim();
  return cleaned ? cleaned.charAt(0).toUpperCase() + cleaned.slice(1) : cleaned;
}

function itemText(item) {
  if (item == null) return "";
  if (typeof item === "string") return actionText(item);
  if (typeof item !== "object") return String(item);
  if (item.what) return actionText(item.what);
  if (item.text) return actionText(item.text);
  if (item.name) {
    const role = String(item.role || "").trim();
    const showRole = role && !/^(?:not (?:mentioned|evident|established|available)|none identified|unknown|n\/a|—)$/i.test(role);
    return normalizeGermanSellerRole(`${item.name}${showRole ? ` · ${role}` : ""}`);
  }
  return normalizeGermanSellerRole(item.answer || item.description || "");
}

function meaningfulItemText(item) {
  const text = String(itemText(item) || "").trim();
  if (!text) return "";
  if (/^(?:not (?:mentioned|evident|established|available)|none identified|n\/a|—)$/i.test(text)) {
    return "";
  }
  return text;
}

function memoryTokens(value) {
  const stopWords = new Set([
    "alla", "allo", "anche", "come", "dalla", "delle", "della", "degli", "dello",
    "into", "from", "that", "the", "this", "with", "pour", "dans", "avec", "para", "con",
  ]);
  return new Set(
    itemText(value)
      .toLocaleLowerCase()
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .match(/[a-z0-9]+/g)
      ?.filter((token) => token.length >= 4 && !stopWords.has(token)) || []
  );
}

function memoryOverlap(left, right) {
  const a = memoryTokens(left);
  const b = memoryTokens(right);
  if (!a.size || !b.size) return false;
  let shared = 0;
  a.forEach((token) => {
    if (b.has(token)) shared += 1;
  });
  return shared / Math.min(a.size, b.size) >= 0.68;
}

function uniqueMemoryItems(items, exclude = [], limit = 8) {
  const kept = [];
  (Array.isArray(items) ? items : []).forEach((item) => {
    if (!meaningfulItemText(item)) return;
    if (exclude.some((other) => memoryOverlap(item, other))) return;
    if (kept.some((other) => memoryOverlap(item, other))) return;
    if (kept.length < limit) kept.push(item);
  });
  return kept;
}

function GroupedMemoryCard({ title, groups, tone = "violet" }) {
  const visibleGroups = groups
    .map((group) => ({ ...group, items: uniqueMemoryItems(group.items, [], 6) }))
    .filter((group) => group.items.length);
  if (!visibleGroups.length) return null;
  return (
    <div className={`relationship-memory-group-card relationship-memory-group-card--${tone}`}>
      <div className="relationship-memory-list__title">{title}</div>
      <div className="relationship-memory-group-card__groups">
        {visibleGroups.map((group) => (
          <div key={group.label}>
            <small>{group.label}</small>
            <ul>
              {group.items.map((item, index) => (
                <li key={index}>
                  <span aria-hidden="true">•</span>
                  <span>{itemText(item)}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function memorySourceCallId(item, fallback = null) {
  if (!item || typeof item !== "object") return fallback;
  return item.source_call_id || item.last_seen_call_id || item.call_id || fallback;
}

function LastObservedLink({
  callId,
  sourceMap,
  relationshipKey,
  relationshipName,
  deepDiveTarget,
  label = "Last observed",
}) {
  if (!callId) return null;
  const source = sourceMap?.[String(callId)] || {};

  const deepLinkSearch = deepDiveTarget
    ? `?deepDiveTarget=${encodeURIComponent(deepDiveTarget)}`
    : "";

  const isDemoCall = String(callId).startsWith("demo-");
  const isInteractive = !isDemoCall || isAvailableDemoCall(callId);

  if (!isInteractive) {
    return (
      <span
        className="relationship-source-link relationship-source-link--disabled"
        title={`${source.title || "Earlier call"} is shown as synthetic context only`}
        aria-disabled="true"
      >
        <span>{label}</span>
        <strong>{source.title || "Supporting call"}</strong>
        {source.date && <small>{fmtDate(source.date)}</small>}
      </span>
    );
  }

  return (
    <Link
      to={isDemoCall
        ? { pathname: demoBasePathForCall(callId), search: deepDiveTarget ? `?section=analysis&deepDiveTarget=${encodeURIComponent(deepDiveTarget)}` : "?section=analysis" }
        : { pathname: `/calls/${callId}`, search: deepLinkSearch }}
      state={isDemoCall ? undefined : {
        from: "relationship",
        relationshipKey,
        relationshipName,
        ...(deepDiveTarget ? { deepDiveTarget } : {}),
      }}
      className="relationship-source-link"
      title={source.title ? `Open ${source.title}` : "Open supporting call"}
    >
      <span>{label}</span>
      <strong>{source.title || "Supporting call"}</strong>
      {source.date && <small>{fmtDate(source.date)}</small>}
      <ArrowRight size={12} weight="bold" aria-hidden="true" />
    </Link>
  );
}

function MemoryList({
  title,
  items,
  tone = "violet",
  card = false,
  icon: Icon,
  sourceMap,
  relationshipKey,
  relationshipName,
  deepDiveTarget,
}) {
  const visible = Array.isArray(items) ? items.filter(meaningfulItemText).slice(0, 6) : [];
  if (!visible.length) return null;

  return (
    <div
      className={`relationship-memory-list relationship-memory-list--${tone}${
        card ? " relationship-memory-list--card" : ""
      }`}
    >
      <div className="relationship-memory-list__title">
        {Icon && <Icon size={13} weight="fill" aria-hidden="true" />}
        <span>{title}</span>
      </div>
      <ul>
        {visible.map((item, index) => {
          const callId = memorySourceCallId(item);
          return (
            <li key={index}>
              <span aria-hidden="true">•</span>
              <span className="relationship-memory-list__item-copy">
                <span>{itemText(item)}</span>
                <LastObservedLink
                  callId={callId}
                  sourceMap={sourceMap}
                  relationshipKey={relationshipKey}
                  relationshipName={relationshipName}
                  deepDiveTarget={deepDiveTarget}
                />
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function relationshipScoreTone(value, invert = false) {
  return scoreToTone(value, { invert });
}

function capitalizeFirstLetter(value) {
  return String(value || "").replace(/\p{L}/u, (letter) => letter.toLocaleUpperCase());
}

function StateMetric({
  label,
  value,
  score,
  icon: Icon,
  tone = "default",
  invert = false,
  className = "",
  sourceCallId,
  sourceMap,
  relationshipKey,
  relationshipName,
  deepDiveTarget,
  sourceLabel = "Last observed",
}) {
  const hasScore = score !== undefined && score !== null;
  const rawShown = value || (hasScore ? scoreToRating(score) : "—");
  const shown = label === "Stance" && !hasScore ? capitalizeFirstLetter(rawShown) : rawShown;
  const metricClass = hasScore
    ? `relationship-state-metric--score relationship-state-metric--score-${relationshipScoreTone(score, invert)}`
    : `relationship-state-metric--${tone}`;

  return (
    <div
      className={`relationship-state-metric ${metricClass} ${className}`.trim()}
    >
      <div className="relationship-state-metric__label">
        {Icon && <Icon size={13} weight="fill" aria-hidden="true" />}
        <span>{label}</span>
      </div>
      <div className={`relationship-state-metric__value${hasScore ? " score-grade" : ""}`}>{shown}</div>
      <LastObservedLink
        callId={sourceCallId}
        sourceMap={sourceMap}
        relationshipKey={relationshipKey}
        relationshipName={relationshipName}
        deepDiveTarget={deepDiveTarget}
        label={sourceLabel}
      />
    </div>
  );
}

const EVOLUTION_STATUS_ALIASES = {
  resolved: "resolved",
  improving: "improving",
  persistent: "persistent",
  critical: "critical",
  recurring: "persistent",
  current: "persistent",
  new: "persistent",
  open: "persistent",
};

function evolutionStep(step) {
  if (typeof step === "string") {
    return { text: normalizeGermanSellerRole(step), status: "persistent" };
  }

  const rawStatus = String(step?.evolution_status || step?.status || "persistent").toLowerCase();
  return {
    text: normalizeGermanSellerRole(step?.text || ""),
    status: EVOLUTION_STATUS_ALIASES[rawStatus] || "persistent",
  };
}

const EVOLUTION_COLOR = {
  resolved: "is-resolved",
  improving: "is-improving",
  persistent: "is-persistent",
  critical: "is-critical",
};

function RelationshipMemory({ memory, onRebuild, rebuilding, milestones, relationshipKey, relationshipName }) {
  if (!memory) return null;

  if (!memory.available || memory.needs_rebuild) {
    return (
      <section id="next-call" className="relationship-memory-empty" data-testid="memory-empty-state">
        <div className="relationship-memory-empty__identity">
          <span className="relationship-memory-empty__icon">
            <ClockCounterClockwise size={22} weight="duotone" />
          </span>
          <div>
            <h2>{memory.needs_rebuild ? "Memory needs a refresh" : "Build the relationship memory"}</h2>
            <p>
              {memory.needs_rebuild
                ? "The memory schema changed. Rebuild the compact state from the stored calls."
                : "Build a compact longitudinal state from the analyses already stored for this customer."}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onRebuild}
          disabled={rebuilding}
          className="relationship-action-button"
          data-testid="build-relationship-memory"
        >
          <ClockCounterClockwise size={15} weight="bold" />
          {rebuilding ? "Building memory..." : memory.needs_rebuild ? "Rebuild memory" : "Build relationship memory"}
        </button>
      </section>
    );
  }

  const state = memory.current_state || {};
  const facts = state.facts || {};
  const inferences = state.inferences || {};
  const brief = memory.next_call_brief || {};

  const commitments = uniqueMemoryItems(
    (facts.commitments || []).filter((item) => item?.status === "open" || item?.status === "unknown"),
  );

  const commercialObjections = uniqueMemoryItems(
    (facts.commercial_objections || facts.explicit_objections || []).filter(
      (item) => !item?.status || item.status === "open",
    ),
  );
  const blockers = uniqueMemoryItems(
    (facts.operational_blockers || []).filter((item) => !item?.status || item.status === "open"),
    commercialObjections,
  );
  const preferences = uniqueMemoryItems(
    (facts.process_preferences || []).filter((item) => !item?.status || item.status === "active"),
    [...commercialObjections, ...blockers],
  );
  const riskSource = Array.isArray(brief.risks) && brief.risks.length
    ? brief.risks
    : inferences.risks;
  const opportunitySource = Array.isArray(brief.opportunities) && brief.opportunities.length
    ? brief.opportunities
    : inferences.opportunities;

  const risks = uniqueMemoryItems(
    riskSource,
    [...commercialObjections, ...blockers, ...preferences, ...commitments],
    6,
  );
  const opportunities = uniqueMemoryItems(
    opportunitySource,
    [...commercialObjections, ...blockers, ...preferences, ...risks, ...commitments],
    6,
  );

  const sourceMap = Object.fromEntries(
    (Array.isArray(milestones) ? milestones : []).map((milestone) => [
      String(milestone.call_id),
      milestone,
    ])
  );
  const statusSourceCallId = state.as_of_call_id || memory.last_processed_call_id;
  const hasWatchItems = Boolean(
    commercialObjections.length || blockers.length || risks.length || opportunities.length
  );
  const stakeholders = uniqueMemoryItems(facts.stakeholders, [], 6).filter((item) => (
    !/^(?:non nominato|unnamed|unknown)\b/i.test(itemText(item))
  ));

  return (
    <section className="relationship-memory relationship-memory--final" data-testid="relationship-memory">
      <PrepareNextCall
        memory={memory}
        customer={{
          id: relationshipKey,
          name: relationshipName,
          call_count: Array.isArray(milestones) ? milestones.length : 0,
        }}
        milestones={milestones}
        demoMode={true}
        onMemoryRefresh={onRebuild}
        memoryRefreshing={rebuilding}
      />

      <article id="relationship-state" className="relationship-state relationship-state--final">
        <div className="relationship-card-header">
          <div><h2>Current relationship state</h2></div>
        </div>

        <div className="relationship-state__section relationship-state__section--snapshot">
          <div className="relationship-state__section-title">Snapshot</div>
          <div className="relationship-state__snapshot relationship-state__snapshot--final">
            <div className="relationship-state__snapshot-copy relationship-state__snapshot-copy--two">
              <MemoryList
                title="Stakeholders"
                icon={UsersThree}
                items={stakeholders}
                tone="violet"
                card
                sourceMap={sourceMap}
                relationshipKey={relationshipKey}
                relationshipName={relationshipName}
              />
              <StateMetric
                label="Status Quo"
                icon={Equals}
                value={facts.customer_position}
                className="relationship-state-metric--summary"
                sourceCallId={statusSourceCallId}
                sourceMap={sourceMap}
                relationshipKey={relationshipKey}
                relationshipName={relationshipName}
                sourceLabel="Last updated"
              />
            </div>

            <div className="relationship-state__score-grid relationship-state__score-grid--three" aria-label="Commercial signals">
              <StateMetric label="Buying intent" icon={CurrencyCircleDollar} score={inferences.buying_intent?.score} />
              <StateMetric label="Churn risk" icon={HeartBreak} score={inferences.churn_risk?.score} invert />
              <StateMetric label="Trust" icon={SealCheck} score={inferences.trust?.score} />
            </div>
          </div>
        </div>

        <div className="relationship-state__details relationship-state__details--final">
          {hasWatchItems && (
            <div className="relationship-state__section">
              <div className="relationship-state__section-title">What to watch</div>
              <div className="relationship-state__watch-grid">
                <MemoryList
                  title="Blockers"
                  icon={WarningCircle}
                  items={blockers}
                  tone="violet"
                  card
                  sourceMap={sourceMap}
                  relationshipKey={relationshipKey}
                  relationshipName={relationshipName}
                  deepDiveTarget="blockers"
                />
                <MemoryList
                  title="Objections"
                  icon={Flag}
                  items={commercialObjections}
                  tone="violet"
                  card
                  sourceMap={sourceMap}
                  relationshipKey={relationshipKey}
                  relationshipName={relationshipName}
                  deepDiveTarget="objections"
                />
                <MemoryList
                  title="Risks"
                  icon={Warning}
                  items={risks}
                  tone="violet"
                  card
                  sourceMap={sourceMap}
                  relationshipKey={relationshipKey}
                  relationshipName={relationshipName}
                  deepDiveTarget="risks"
                />
                <MemoryList
                  title="Opportunities"
                  icon={Lightbulb}
                  items={opportunities}
                  tone="violet"
                  card
                  sourceMap={sourceMap}
                  relationshipKey={relationshipKey}
                  relationshipName={relationshipName}
                  deepDiveTarget="opportunities"
                />
              </div>
            </div>
          )}

          <div className="relationship-state__section">
            <div className="relationship-state__section-title">Human dynamics</div>
            <div className="relationship-state__health-grid">
              <StateMetric label="Relationship score" icon={LinkSimple} score={inferences.relationship_strength?.score} />
              <StateMetric
                label="Stance"
                icon={Handshake}
                value={inferences.attitude?.text}
                sourceCallId={memorySourceCallId(inferences.attitude, statusSourceCallId)}
                sourceMap={sourceMap}
                relationshipKey={relationshipKey}
                relationshipName={relationshipName}
                deepDiveTarget="stance"
              />
              <StateMetric
                label="Tone"
                icon={ChatCircle}
                value={inferences.emotional_tone?.text}
                sourceCallId={memorySourceCallId(inferences.emotional_tone, statusSourceCallId)}
                sourceMap={sourceMap}
                relationshipKey={relationshipKey}
                relationshipName={relationshipName}
                deepDiveTarget="tone"
              />
            </div>
          </div>
        </div>
      </article>
    </section>
  );
}



function LiveDemoRelationshipMobileHeader({ call }) {
  return (
    <div className="relationship-demo-mobile-header">
      <header className="public-demo-header" aria-label="Callsect interactive demo">
        <div className="public-demo-header__inner">
          <Link to="/" className="brand-lockup-link" aria-label="Back to Callsect website">
            <BrandLockup className="brand-lockup--landing" variant="auto" />
          </Link>
          <div className="public-demo-header__actions">
            <PublicThemeControl />
            <Link to="/" className="public-demo-header__back" aria-label="Back to homepage">
              <ArrowLeft size={14} weight="bold" aria-hidden="true" />
              <span>Back to homepage</span>
            </Link>
          </div>
        </div>
      </header>

      <div className="relationship-demo-mobile-header__controls">
        <span className="relationship-demo-mobile-header__badge">Interactive demo</span>
        <Link to="/demo" className="relationship-demo-mobile-header__restart">
          <ArrowLeft size={13} weight="bold" aria-hidden="true" />
          Back to demo selection
        </Link>
      </div>

      <section className="relationship-demo-mobile-header__call">
        <span className="relationship-demo-mobile-header__call-icon">
          <PhoneCall size={20} weight="duotone" aria-hidden="true" />
        </span>
        <div className="relationship-demo-mobile-header__call-copy">
          <div className="relationship-demo-mobile-header__eyebrow">Synthetic data</div>
          <h1>{call?.title || "Customer conversation"}</h1>
          <div className="relationship-demo-mobile-header__meta">
            <span>{call?.customer_name}</span>
            <span>{fmtDuration(call?.duration)}</span>
          </div>
        </div>
      </section>

      <div className="relationship-demo-mobile-header__progress" aria-label="Step 4 of 4">
        {[0, 1, 2, 3].map((index) => (
          <span key={index} aria-hidden="true" />
        ))}
      </div>
    </div>
  );
}

function fmtDuration(seconds) {
  const value = Number(seconds || 0);
  if (!Number.isFinite(value) || value <= 0) return "";
  const minutes = Math.floor(value / 60);
  const remainder = Math.floor(value % 60);
  return `${minutes}:${String(remainder).padStart(2, "0")}`;
}

export default function LiveDemoRelationshipPage({ demoUseCase = "renewals" }) {
  const demoMode = true;
  const demoRelationshipData = demoUseCase === "sales" ? SALES_DEMO_RELATIONSHIP_DATA : DEMO_RELATIONSHIP_DATA;
  const demoCall = demoUseCase === "sales" ? SALES_DEMO_CALL_BASE : DEMO_CALL_BASE;
  const demoBasePath = `/demo/${demoUseCase === "sales" ? "sales" : "renewals"}`;
  const demoActiveCallId = (
    demoRelationshipData.relationship_memory?.last_processed_call_id
    || demoRelationshipData.milestones?.[0]?.call_id
  );
  const { key: routeKey } = useParams();
  const key = demoMode ? demoRelationshipData.customer.key : routeKey;
  const [data, setData] = useState(() => demoMode ? demoRelationshipData : null);
  const [loading, setLoading] = useState(!demoMode);
  const [loadError, setLoadError] = useState("");
  const [rebuilding, setRebuilding] = useState(false);
  const [showAllCalls, setShowAllCalls] = useState(() => {
    if (typeof window === "undefined") return true;
    return !window.matchMedia("(max-width: 700px)").matches;
  });
  const [isMobileDemo, setIsMobileDemo] = useState(() => (
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 760px)").matches
      : false
  ));
  const nav = useNavigate();
  const location = useLocation();
  const highlightedCallRef = useRef(false);
  const betaCtaRef = useRef(null);
  const betaCtaViewedRef = useRef(false);
  const relationshipReachedRef = useRef(false);
  const activeCallId = location.state?.activeCallId;


  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const query = window.matchMedia("(max-width: 760px)");
    const update = (event) => setIsMobileDemo(event.matches);
    setIsMobileDemo(query.matches);
    query.addEventListener?.("change", update);
    return () => query.removeEventListener?.("change", update);
  }, []);

  useEffect(() => {
    if (!demoMode || typeof window === "undefined") return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [demoMode, location.pathname]);

  useEffect(() => {
    if (!demoMode || isMobileDemo || typeof window === "undefined") return;
    const raw = Number(window.sessionStorage.getItem(`callsect:live-demo-unlocked:${demoUseCase}`));
    const unlockedIndex = Number.isFinite(raw) ? Math.max(0, Math.min(4, raw)) : 0;
    if (unlockedIndex >= 4) return;
    const fallbackSections = ["transcript", "analysis", "coaching", "notes"];
    nav(`${demoBasePath}?section=${fallbackSections[Math.min(unlockedIndex, 3)]}`, { replace: true });
  }, [demoBasePath, demoMode, demoUseCase, isMobileDemo, nav]);

  const restartDemo = () => {
    if (!demoMode) return;
    if (typeof window !== "undefined") {
      window.sessionStorage.removeItem("callsect:live-demo-stage");
      if (!isMobileDemo) window.sessionStorage.setItem(`callsect:live-demo-unlocked:${demoUseCase}`, "0");
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
    nav(isMobileDemo ? demoBasePath : `${demoBasePath}?section=transcript`);
  };

  const trackDemoSignup = () => {
    trackPublicEvent("cta_click", {
      source: "demo",
      section: "relationship-gate",
      cta_id: "sign_up_free",
      use_case: demoUseCase,
    });
  };

  const openDemoFlowStep = (step) => {
    if (!demoMode) return;
    if (step === "relationship") return;

    const stageForStep = {
      transcript: 0,
      analysis: 1,
      notes: 2,
      coaching: 3,
    };
    const nextStage = stageForStep[step] ?? 0;
    if (typeof window !== "undefined") {
      const currentStage = Number(window.sessionStorage.getItem("callsect:live-demo-stage") || 0);
      window.sessionStorage.setItem("callsect:live-demo-stage", String(Math.max(currentStage, nextStage)));
      window.sessionStorage.setItem(`callsect:live-demo-unlocked:${demoUseCase}`, "4");
    }
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
    nav(`${demoBasePath}?section=${encodeURIComponent(step)}`);
  };

  const loadTimeline = async () => {
    if (demoMode) {
      setData(demoRelationshipData);
      setLoading(false);
      setLoadError("");
      return;
    }
    setLoading(true);
    setLoadError("");
    try {
      const { data } = await api.get(`/customers/${encodeURIComponent(key)}/timeline`);
      setData(data);
    } catch (error) {
      const message = getActionableError(error, "Callsect could not load this relationship timeline.");
      setLoadError(message);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTimeline();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demoMode, key]);

  useEffect(() => {
    if (!data || !activeCallId) return;
    const sortedMilestones = [...(data.milestones || [])].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
    const activeIndex = sortedMilestones.findIndex(
      (milestone) => String(milestone.call_id) === String(activeCallId)
    );
    if (activeIndex >= 3) setShowAllCalls(true);
  }, [activeCallId, data]);

  useEffect(() => {
    if (!data || !activeCallId || highlightedCallRef.current) return;
    const frame = window.requestAnimationFrame(() => {
      const target = document.getElementById(`timeline-call-${activeCallId}`);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "center" });
        highlightedCallRef.current = true;
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [activeCallId, data, showAllCalls]);

  useEffect(() => {
    if (!demoMode || relationshipReachedRef.current || typeof window === "undefined") return;

    relationshipReachedRef.current = true;
    const completedStage = Number(window.sessionStorage.getItem("callsect:live-demo-stage") || 0);
    trackUmamiEvent("demo_relationship_reached", {
      source: "demo",
      section: "relationship-gate",
      page_path: window.location.pathname || `${demoBasePath}/relationship`,
      use_case: demoUseCase,
      flow_stage: completedStage,
    });
  }, [demoBasePath, demoMode, demoUseCase]);

  useEffect(() => {
    if (!demoMode || !betaCtaRef.current || betaCtaViewedRef.current) return;
    if (typeof window === "undefined" || !("IntersectionObserver" in window)) return;

    const target = betaCtaRef.current;
    const observer = new window.IntersectionObserver(
      (entries) => {
        const visible = entries.some(
          (entry) => entry.isIntersecting && entry.intersectionRatio >= 0.55
        );
        if (!visible || betaCtaViewedRef.current) return;

        betaCtaViewedRef.current = true;
        trackUmamiEvent("demo_cta_viewed", {
          source: "demo",
          section: "relationship-gate",
          cta_id: "sign_up_free",
          page_path: window.location.pathname || demoBasePath,
          use_case: demoUseCase,
        });
        observer.disconnect();
      },
      { threshold: [0.55] }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [demoBasePath, demoMode, demoUseCase]);

  const rebuildMemory = async () => {
    if (demoMode) {
      toast.success("Synthetic relationship memory is already up to date");
      return;
    }
    const relationshipId = data?.customer?.id || key;
    setRebuilding(true);

    try {
      const result = await api.post(`/customers/${encodeURIComponent(relationshipId)}/memory/rebuild`);
      const refreshed = await api.get(`/customers/${encodeURIComponent(relationshipId)}/timeline`);
      setData(refreshed.data);
      toast.success(`Relationship memory built from ${result.data.processed_calls} calls`);
    } catch (err) {
      toast.error(getActionableError(err, "Relationship memory could not be rebuilt."));
    } finally {
      setRebuilding(false);
    }
  };


  if (loading) {
    return (
      <div className={`relationship-page${demoMode ? " public-demo-page" : ""}`}>
        {demoMode ? <PublicDemoNav useCaseLabel={demoUseCase === "sales" ? "Sales path" : "Retention path"} /> : <Nav />}
        <main className={`relationship-shell product-page-enter${demoMode ? " relationship-shell--demo" : ""}`}>
          <div className="relationship-page-loading">
            <div className="relationship-page-loading__hero" />
            <div className="relationship-page-loading__panel" />
            <div className="relationship-page-loading__panel" />
          </div>
        </main>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={`relationship-page${demoMode ? " public-demo-page" : ""}`}>
        {demoMode ? <PublicDemoNav useCaseLabel={demoUseCase === "sales" ? "Sales path" : "Retention path"} /> : <Nav />}
        <main className={`relationship-shell product-page-enter${demoMode ? " relationship-shell--demo" : ""}`}>
          <div className="relationship-not-found">
            {loadError ? <ArrowClockwise size={24} weight="duotone" /> : <Warning size={24} weight="duotone" />}
            <strong>{loadError ? "This relationship could not be loaded" : "Relationship not found"}</strong>
            {loadError && <p>{loadError}</p>}
            <div className="relationship-not-found__actions">
              {loadError && (
                <button type="button" onClick={loadTimeline}>
                  <ArrowClockwise size={15} weight="bold" />
                  Try again
                </button>
              )}
              <button type="button" onClick={() => nav("/customers")}>Back to customers</button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const { customer, milestones, objection_evolution, relationship_memory } = data;
  const newestFirstMilestones = [...milestones].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );
  const compactMilestones = newestFirstMilestones.slice(0, 3);
  const historyButtonLabel = showAllCalls
    ? "Compact history"
    : "Expand history";

  if (demoMode) {
    const prep = relationship_memory?.prepare_next_call || {};
    const questionCount = Array.isArray(prep.questions_to_ask) ? prep.questions_to_ask.length : 0;
    const objectionCount = Array.isArray(prep.likely_objections) ? prep.likely_objections.length : 0;
    const flowCount = Array.isArray(prep.suggested_call_flow) ? prep.suggested_call_flow.length : 0;

    return (
      <div className={`relationship-page${demoMode ? " public-demo-page" : ""}`}>
        <div className="relationship-demo-desktop-nav">
          <PublicDemoNav useCaseLabel={demoUseCase === "sales" ? "Sales path" : "Retention path"} />
        </div>

        <LiveDemoRelationshipMobileHeader call={demoCall} />

        <main className="relationship-shell product-page-enter relationship-shell--demo relationship-shell--conversion-gate">
          <Link to="/demo" className="live-demo-back-selection relationship-demo-back-selection">
            <ArrowLeft size={14} weight="bold" aria-hidden="true" />
            Back to demo selection
          </Link>

          <div className="relationship-demo-desktop-flow">
            <div className="live-demo-flow-anchor live-demo-flow-anchor--relationship">
              <LiveDemoFlowNav
                activeStep="relationship"
                completedSteps={["transcript", "analysis", "coaching", "notes"]}
                maxUnlockedIndex={4}
                onStepSelect={openDemoFlowStep}
              />
            </div>
          </div>

          <section className="relationship-hero">
            <div className="relationship-hero__content">
              <span className="relationship-hero__avatar">
                <UserCircle size={31} weight="duotone" />
              </span>

              <div className="relationship-hero__copy">
                <h1>{customer.name}</h1>
                <div className="relationship-hero__meta">
                  {customer.company && (
                    <span>
                      <Buildings size={14} />
                      {customer.company}
                    </span>
                  )}
                  <span>
                    <Phone size={14} />
                    {customer.call_count} call{customer.call_count === 1 ? "" : "s"}
                  </span>
                  <span>
                    <ClockCounterClockwise size={14} />
                    {fmtDate(customer.first_call_at)} → {fmtDate(customer.last_call_at)}
                  </span>
                </div>
                <RelationshipBookmark
                  bookmark={relationship_memory?.current_state?.bookmark}
                  className="relationship-hero__bookmark"
                />
              </div>
            </div>
          </section>

          <section className="live-demo-relationship-conversion" aria-labelledby="live-demo-relationship-gate-title">
            <div className="live-demo-relationship-conversion__preview" inert={true} aria-hidden="true">
              <section className="relationship-history relationship-history--priority">
                <div className="relationship-history__header">
                  <div><h2>Conversation history</h2></div>
                  <div className="relationship-history__actions"><span>{newestFirstMilestones.length} calls</span></div>
                </div>
                <ConversationHistoryTimeline
                  milestones={compactMilestones}
                  objectionEvolution={objection_evolution}
                  relationshipCallCount={newestFirstMilestones.length}
                  activeCallId={demoActiveCallId}
                  allCollapsed={false}
                />
              </section>

              <section className="live-demo-relationship-conversion__next-preview">
                <div className="relationship-card-header"><div><h2>Prepare next conversation</h2></div></div>
                <div className="live-demo-relationship-conversion__next-grid">
                  <article><small>Likely objections</small><strong>{objectionCount || 3} prepared responses</strong></article>
                  <article><small>Questions to ask</small><strong>{questionCount || 4} evidence-led prompts</strong></article>
                  <article><small>Call flow</small><strong>{flowCount || 5} decision-focused steps</strong></article>
                </div>
              </section>
            </div>

            <div className="live-demo-relationship-conversion__veil" aria-hidden="true" />

            <div className="live-demo-relationship-conversion__gate">
              <span className="live-demo-relationship-conversion__lock" aria-hidden="true">
                <LockSimple size={23} weight="fill" />
              </span>
              <div className="brand-eyebrow">Relationship intelligence</div>
              <h2 id="live-demo-relationship-gate-title">Found this useful?</h2>
              <p>
                <span className="live-demo-relationship-conversion__copy-line">You’ve seen the call, the Dissection, grounded Coaching Moves, and a CRM Note you'd write.</span>
                <span className="live-demo-relationship-conversion__copy-line">Now see what happens when Callsect carries that understanding into what comes next.</span>
              </p>


              <div className="live-demo-relationship-conversion__unlock-list" aria-label="What beta access unlocks">
                <span><CheckCircle size={16} weight="fill" />Relationship memory that builds across every conversation</span>
                <span><CheckCircle size={16} weight="fill" />What changed since the last call — and what still matters</span>
                <span><CheckCircle size={16} weight="fill" />A next-call plan and script you can reshape with new context</span>
                <span><CheckCircle size={16} weight="fill" />CRM Notes and follow-up emails written in your style</span>
              </div>

              <Link ref={betaCtaRef} to="/login?mode=register" className="live-demo-beta-cta__button" onClick={trackDemoSignup}>
                <span>Sign up free</span>
                <ArrowRight size={22} weight="bold" aria-hidden="true" />
              </Link>
              <small>No card. No plans. Free to use.</small>
            </div>
          </section>

          <LiveDemoNextAction label="Restart demo" final onClick={restartDemo} />
        </main>

      </div>
    );
  }

  return (
    <div className={`relationship-page${demoMode ? " public-demo-page" : ""}`}>
      {demoMode ? <PublicDemoNav useCaseLabel={demoUseCase === "sales" ? "Sales path" : "Retention path"} /> : <Nav />}

      <main className={`relationship-shell product-page-enter${demoMode ? " relationship-shell--demo" : ""}`}>
        {!demoMode && (
        <div className="relationship-topbar">

          <Link to={demoMode ? `${demoBasePath}?section=notes` : "/upload"} className="relationship-upload-button">
            {demoMode ? <ArrowRight size={14} weight="bold" /> : <Plus size={14} weight="bold" />}
            {demoMode ? "Back to guided call" : "Upload another call"}
          </Link>
        </div>
        )}

        {demoMode && (
          <div className="live-demo-flow-anchor live-demo-flow-anchor--relationship">
            <LiveDemoFlowNav
              activeStep="relationship"
              completedSteps={["transcript", "analysis", "coaching", "notes"]}
              maxUnlockedIndex={4}
              onStepSelect={openDemoFlowStep}
            />
          </div>
        )}

        <section className="relationship-hero">
          <div className="relationship-hero__content">
            <span className="relationship-hero__avatar">
              <UserCircle size={31} weight="duotone" />
            </span>

            <div className="relationship-hero__copy">
              <h1>{customer.name}</h1>
              <div className="relationship-hero__meta">
                {customer.company && (
                  <span>
                    <Buildings size={14} />
                    {customer.company}
                  </span>
                )}
                <span>
                  <Phone size={14} />
                  {customer.call_count} call{customer.call_count === 1 ? "" : "s"}
                </span>
                <span>
                  <ClockCounterClockwise size={14} />
                  {fmtDate(customer.first_call_at)} → {fmtDate(customer.last_call_at)}
                </span>
              </div>
              <RelationshipBookmark
                bookmark={relationship_memory?.current_state?.bookmark}
                className="relationship-hero__bookmark"
              />
            </div>
          </div>
        </section>

        <nav className="relationship-section-nav" aria-label="Relationship sections">
          <a href="#conversation-history">
            <Phone size={15} weight="duotone" />
            Conversation history
            <span>{newestFirstMilestones.length}</span>
          </a>
          <a href="#next-call">
            <CheckCircle size={15} weight="duotone" />
            Next call
          </a>
          <a href="#relationship-state">
            <UserCircle size={15} weight="duotone" />
            Relationship state
          </a>
          {newestFirstMilestones.length >= 2 && objection_evolution && objection_evolution.length > 0 && (
            <a href="#objections">
              <Flag size={15} weight="duotone" />
              Objections
            </a>
          )}
        </nav>

        <section
          id="conversation-history"
          className="relationship-history relationship-history--priority"
        >
          <div className="relationship-history__header">
            <div>
              <h2>Conversation history</h2>
            </div>

            <div className="relationship-history__actions">
              <span>
                {newestFirstMilestones.length} call{newestFirstMilestones.length === 1 ? "" : "s"}
              </span>
              {newestFirstMilestones.length > 0 && (
                <button
                  type="button"
                  className="relationship-history__toggle"
                  onClick={() => setShowAllCalls((current) => !current)}
                  aria-expanded={showAllCalls}
                >
                  {historyButtonLabel}
                  <CaretDown size={14} weight="bold" aria-hidden="true" />
                </button>
              )}
            </div>
          </div>

          <ConversationHistoryTimeline
            milestones={showAllCalls ? newestFirstMilestones : compactMilestones}
            objectionEvolution={objection_evolution}
            relationshipCallCount={newestFirstMilestones.length}
            activeCallId={activeCallId}
            allCollapsed={!showAllCalls}
            getCallLink={(milestone) => ({
              to: `${demoBasePath}?section=analysis`,
              disabled: !isAvailableDemoCall(milestone.call_id, demoActiveCallId),
            })}
          />

          <aside
            className="relationship-memory-tip relationship-memory-tip--embedded relationship-memory-tip--cta relationship-memory-tip--placeholder"
            data-testid="relationship-memory-tip"
            aria-label="Add the next call is disabled in the public demo"
            aria-disabled="true"
          >
            <span className="relationship-memory-tip__icon" aria-hidden="true">
              <Plus size={20} weight="bold" />
            </span>
            <span className="relationship-memory-tip__copy">
              <strong>Add the next call</strong>
            </span>
            <span className="relationship-memory-tip__action" aria-hidden="true">
              <ArrowRight size={18} weight="bold" />
            </span>
          </aside>
        </section>

        <RelationshipMemory
          memory={relationship_memory}
          onRebuild={rebuildMemory}
          rebuilding={rebuilding}
          milestones={newestFirstMilestones}
          relationshipKey={key}
          relationshipName={customer.name}
        />

        {newestFirstMilestones.length >= 2 && objection_evolution && objection_evolution.length > 0 && (
          <section id="objections" className="objection-evolution" data-testid="evolution-chain">
            <div className="relationship-card-header">
              <div>
                <h2>Objection evolution</h2>
              </div>
            </div>

            <div className="objection-evolution__chain">
              {objection_evolution.map((rawStep, index) => {
                const step = evolutionStep(rawStep);

                return (
                  <div key={index} className="objection-evolution__step">
                    <span
                      data-testid={`evolution-step-${index}`}
                      data-status={step.status}
                      title={`${step.status}: ${step.text}`}
                      className={`objection-evolution__chip ${EVOLUTION_COLOR[step.status] || EVOLUTION_COLOR.persistent}`}
                    >
                      <Flag className="objection-evolution__flag" size={13} weight="fill" />
                      <span className="objection-evolution__text">{step.text}</span>
                    </span>

                    {index < objection_evolution.length - 1 && (
                      <span className="objection-evolution__connector" aria-hidden="true">
                        <span />
                        <ArrowDown size={14} weight="bold" />
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {demoMode && (
          <>
            <LiveDemoNextAction
              label="Restart demo"
              final
              onClick={restartDemo}
            />

            <section ref={betaCtaRef} className="live-demo-beta-cta" aria-labelledby="live-demo-beta-cta-title">
              <h2 id="live-demo-beta-cta-title">Found this useful?</h2>
              <p>Try it with one of your own calls.</p>
              <Link
                to="/login?mode=register"
                className="live-demo-beta-cta__button"
                onClick={() => {
                  trackPublicEvent("cta_click", {
                    source: "demo",
                    section: "relationship-gate",
                    cta_id: "sign_up_free",
                    use_case: demoUseCase,
                  });
                }}
              >
                <span>Sign up free</span>
                <ArrowRight size={22} weight="bold" aria-hidden="true" />
              </Link>
            </section>
          </>
        )}
      </main>

    </div>
  );
}
